import { useEffect, useState } from 'react'
import { adminRequest } from './EshopCategories'
import { DateField, formatDay, todayIso } from './DatePicker'

/** Otváracie hodiny prevádzky – podľa nich e-shop ponúka termíny odberu a doručenia. */
export type Interval = { from: string; to: string }
export type OpeningHours = { week: Record<string, Interval[]>; closures: Array<{ date: string; note?: string | null }> }

const DAYS = [['1', 'Pondelok', 'Po'], ['2', 'Utorok', 'Ut'], ['3', 'Streda', 'St'], ['4', 'Štvrtok', 'Št'], ['5', 'Piatok', 'Pi'], ['6', 'Sobota', 'So'], ['7', 'Nedeľa', 'Ne']] as const
const TIMES = Array.from({ length: (23 - 5) * 4 + 1 }, (_, i) => { const m = 5 * 60 + i * 15; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}` })

export const DEFAULT_HOURS: OpeningHours = {
  week: { '1': [{ from: '08:00', to: '18:00' }], '2': [{ from: '08:00', to: '18:00' }], '3': [{ from: '08:00', to: '18:00' }], '4': [{ from: '08:00', to: '18:00' }], '5': [{ from: '08:00', to: '18:00' }], '6': [{ from: '08:00', to: '12:00' }], '7': [] },
  closures: [],
}

/** Krátky súhrn, napr. „Po–Pi 8:00–18:00 · So 8:00–12:00 · Ne zatvorené“. */
export function summarizeHours(hours: OpeningHours | null | undefined): string {
  if (!hours) return 'Nezadané'
  const label = (k: string) => { const v = hours.week[k] ?? []; return v.length ? v.map(i => `${i.from.replace(/^0/, '')}–${i.to.replace(/^0/, '')}`).join(', ') : 'zatvorené' }
  const groups: Array<{ from: string; to: string; text: string }> = []
  for (const [k, , short] of DAYS) {
    const text = label(k); const last = groups[groups.length - 1]
    if (last && last.text === text) last.to = short; else groups.push({ from: short, to: short, text })
  }
  return groups.map(g => `${g.from === g.to ? g.from : `${g.from}–${g.to}`} ${g.text}`).join(' · ')
}

const clone = (h: OpeningHours): OpeningHours => JSON.parse(JSON.stringify(h))
const same = (a: OpeningHours | null, b: OpeningHours | null) => JSON.stringify(a) === JSON.stringify(b)

function validate(h: OpeningHours): string {
  for (const [k, name] of DAYS) {
    const list = h.week[k] ?? []
    for (const i of list) if (i.from >= i.to) return `${name}: čas „do“ musí byť neskôr ako „od“.`
    if (list.length === 2 && !(list[0].to <= list[1].from || list[1].to <= list[0].from)) return `${name}: časy sa prekrývajú.`
  }
  return ''
}

export default function OpeningHoursEditor({ storeId, value, onSaved }: { storeId: string; value: OpeningHours | null; onSaved: (hours: OpeningHours | null) => void }) {
  const [draft, setDraft] = useState<OpeningHours | null>(value ? clone(value) : null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [newClosure, setNewClosure] = useState({ date: '', note: '' })
  useEffect(() => { setDraft(value ? clone(value) : null) }, [value])

  const changed = !same(draft, value)
  const problem = draft ? validate(draft) : ''

  const setDay = (k: string, list: Interval[]) => draft && setDraft({ ...draft, week: { ...draft.week, [k]: list } })
  const copyToWorkdays = () => draft && setDraft({ ...draft, week: { ...draft.week, ...Object.fromEntries(['2', '3', '4', '5'].map(k => [k, clone(draft).week['1']])) } })

  async function save() {
    setSaving(true); setError(''); setMessage('')
    try {
      const data = await adminRequest(`/api/admin/stores/${storeId}/opening-hours`, { method: 'PUT', body: JSON.stringify({ openingHours: draft }) })
      onSaved(data.openingHours ?? null)
      setMessage('Otváracie hodiny sú uložené.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie sa nepodarilo.') } finally { setSaving(false) }
  }

  if (!draft) return <div className="oh-empty">
    <p>Otváracie hodiny nie sú zadané – e-shop zatiaľ ponúka termíny 7:00–19:00 každý deň.</p>
    <button type="button" className="primary-btn" onClick={() => setDraft(clone(DEFAULT_HOURS))}>Nastaviť otváracie hodiny</button>
  </div>

  const upcoming = draft.closures.filter(c => c.date >= todayIso())
  return <div className="oh">
    <div className="oh-week">{DAYS.map(([k, name]) => {
      const list = draft.week[k] ?? []
      const open = list.length > 0
      return <div key={k} className={`oh-row ${open ? '' : 'is-closed'}`}>
        <label className="oh-switch">
          <input type="checkbox" checked={open} onChange={e => setDay(k, e.target.checked ? [{ from: '08:00', to: '18:00' }] : [])} />
          <span aria-hidden="true" /><strong>{name}</strong>
        </label>
        {open ? <div className="oh-intervals">
          {list.map((interval, i) => <span key={i} className="oh-interval">
            <select aria-label={`${name} od`} value={interval.from} onChange={e => setDay(k, list.map((x, j) => j === i ? { ...x, from: e.target.value } : x))}>{TIMES.map(t => <option key={t}>{t}</option>)}</select>
            <span>–</span>
            <select aria-label={`${name} do`} value={interval.to} onChange={e => setDay(k, list.map((x, j) => j === i ? { ...x, to: e.target.value } : x))}>{TIMES.map(t => <option key={t}>{t}</option>)}</select>
            {list.length === 2 && <button type="button" className="oh-x" aria-label="Odstrániť časový úsek" onClick={() => setDay(k, list.filter((_, j) => j !== i))}>×</button>}
          </span>)}
          {list.length === 1 && <button type="button" className="pa-link oh-add" onClick={() => setDay(k, [{ ...list[0], to: '12:00' }, { from: '13:00', to: list[0].to > '13:00' ? list[0].to : '18:00' }])}>+ prestávka</button>}
        </div> : <span className="oh-closed">Zatvorené</span>}
      </div>
    })}</div>
    <div className="oh-tools"><button type="button" className="pa-link" onClick={copyToWorkdays}>Pondelok skopírovať na Ut–Pi</button></div>

    <div className="oh-closures">
      <h4>Zatvorené vo vybrané dni <small>– sviatky, dovolenka</small></h4>
      {upcoming.length ? <ul>{upcoming.map(c => <li key={c.date}>
        <strong>{formatDay(c.date)}</strong>{c.note && <span>{c.note}</span>}
        <button type="button" className="oh-x" aria-label="Odstrániť" onClick={() => setDraft({ ...draft, closures: draft.closures.filter(x => x.date !== c.date) })}>×</button>
      </li>)}</ul> : <p className="oh-muted">Žiadne mimoriadne zatvorené dni.</p>}
      <div className="oh-add-closure">
        <DateField value={newClosure.date} onChange={date => setNewClosure({ ...newClosure, date })} min={todayIso()} placeholder="Deň" isDisabled={d => draft.closures.some(c => c.date === d)} />
        <input value={newClosure.note} maxLength={80} placeholder="Dôvod (nepovinné), napr. Dovolenka" onChange={e => setNewClosure({ ...newClosure, note: e.target.value })} />
        <button type="button" className="pa-ghost" disabled={!newClosure.date} onClick={() => { setDraft({ ...draft, closures: [...draft.closures, { date: newClosure.date, note: newClosure.note.trim() || null }].sort((a, b) => a.date.localeCompare(b.date)) }); setNewClosure({ date: '', note: '' }) }}>Pridať</button>
      </div>
    </div>

    {problem && <p className="st-hint is-warn">{problem}</p>}
    {error && <p className="error-message" role="alert">{error}</p>}
    {message && !changed && <p className="pa-message" role="status">{message}</p>}
    <div className="oh-actions">
      {value && changed && <button type="button" className="pa-ghost" onClick={() => setDraft(clone(value))}>Zrušiť zmeny</button>}
      {!value && <button type="button" className="pa-ghost" onClick={() => setDraft(null)}>Zrušiť</button>}
      <button type="button" className="primary-btn" disabled={!changed || saving || Boolean(problem)} onClick={() => void save()}>{saving ? 'Ukladám…' : changed ? 'Uložiť hodiny' : 'Uložené'}</button>
    </div>
  </div>
}
